import { 
  collection, 
  doc,
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  getDoc,
  arrayUnion,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { User, CreateUserInput, UpdateUserInput } from '../types/users';
import { createActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { removeUndefined } from '@/utils/removeUndefined';
// Removed broken import

interface GetUsersResult {
  users: User[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
}

export const getUsers = async ({
  workspaceId,
  pageSize,
  lastDoc,
  firestoreUser
}: {
  workspaceId: string;
  pageSize: number;
  lastDoc?: QueryDocumentSnapshot;
  firestoreUser: User;
}): Promise<GetUsersResult> => {
  console.log('[API] getUsers called', { workspaceId, pageSize, lastDoc, firestoreUser });

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const usersRef = collection(db, 'users');
    const filterConstraints = [where('workspace.id', '==', workspaceId)];

    let q = query(usersRef, ...filterConstraints);

    if (pageSize) {
      q = query(q, limit(pageSize));
    }

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const users = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        uid: data.uid,
        email: data.email,
        displayName: data.displayName,
        username: data.username,
        status: data.status || 'active',
        roles: data.roles || [],
        contacts: data.contacts || [],
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate(),
        workspace: {
          id: data.workspace.id,
          name: data.workspace.name,
          slug: data.workspace.slug
        },
        language: data.language || 'es',
        currency: data.currency || 'CLP',
        profilePicture: data.profilePicture,
        phoneNumber: data.phoneNumber,
        activityRecords: data.activityRecords || []
      } as User;
    });

    return {
      users,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null
    };
  } catch (error) {
    console.error('Error fetching users:', error);
    throw new Error('Failed to fetch users');
  }
};

/**
 * Fetch all user profiles (workspace memberships) for a Firebase UID.
 * Used for workspace selection after login/register.
 */
export const getUsersByUid = async (uid: string): Promise<User[]> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('uid', '==', uid));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        uid: data.uid,
        email: data.email,
        displayName: data.displayName,
        username: data.username || data.email?.split('@')[0] || '',
        status: data.status || 'active',
        createdAt: data.createdAt?.toDate?.() || new Date(),
        lastLogin: data.lastLogin?.toDate?.(),
        contacts: data.contacts || [],
        workspace: data.workspace
          ? { id: data.workspace.id, name: data.workspace.name, slug: data.workspace.slug }
          : undefined,
        roles: data.roles || [],
        language: data.language || 'en',
        currency: data.currency || 'USD',
        profilePicture: data.profilePicture,
        phoneNumber: data.phoneNumber,
        activityRecords: data.activityRecords || [],
        pendingWorkspace: data.pendingWorkspace
          ? {
              apartmentName: data.pendingWorkspace.apartmentName,
              country: data.pendingWorkspace.country,
              region: data.pendingWorkspace.region,
            }
          : undefined,
      } as User;
    });
  } catch (error) {
    console.error('Error fetching users by uid:', error);
    return [];
  }
};

export const getUserById = async (uid: string, firestoreUser: User): Promise<User | null> => {
  console.log('[API] getUserById called', { uid, firestoreUser });

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('uid', '==', uid),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();
    
    return {
      id: doc.id,
      uid: data.uid,
      email: data.email,
      displayName: data.displayName,
      username: data.username || data.email.split('@')[0],
      status: data.status || 'active',
      createdAt: data.createdAt?.toDate() || new Date(),
      lastLogin: data.lastLogin?.toDate(),
      contacts: data.contacts || [],
      workspace: {
        id: data.workspace.id,
        name: data.workspace.name,
        slug: data.workspace.slug
      },
      roles: data.roles || [],
      language: data.language || 'es',
      currency: data.currency || 'CLP',
      profilePicture: data.profilePicture,
      phoneNumber: data.phoneNumber,
      activityRecords: data.activityRecords || []
    };
  } catch (error) {
    console.error('Error fetching user:', error);
    throw new Error('Failed to fetch user');
  }
};

export const getUserByDocumentId = async (documentId: string, firestoreUser: User): Promise<User | null> => {
  console.log('[API] getUserByDocumentId called', { documentId, firestoreUser });

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get user document directly by ID
    const userRef = doc(db, 'users', documentId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return null;
    }

    const data = userDoc.data();
    
    // Verify workspace access
    if (data.workspace?.id !== firestoreUser.workspace.id) {
      throw new Error('User not found in current workspace');
    }
    
    return {
      id: userDoc.id,
      uid: data.uid,
      email: data.email,
      displayName: data.displayName,
      username: data.username || data.email.split('@')[0],
      status: data.status || 'active',
      createdAt: data.createdAt?.toDate() || new Date(),
      lastLogin: data.lastLogin?.toDate(),
      contacts: data.contacts || [],
      workspace: {
        id: data.workspace.id,
        name: data.workspace.name,
        slug: data.workspace.slug
      },
      roles: data.roles || [],
      language: data.language || 'es',
      currency: data.currency || 'CLP',
      profilePicture: data.profilePicture,
      phoneNumber: data.phoneNumber,
      activityRecords: data.activityRecords || []
    };
  } catch (error) {
    console.error('Error fetching user by document ID:', error);
    throw new Error('Failed to fetch user');
  }
};

export const addUser = async (userData: CreateUserInput): Promise<User> => {
  console.log('[API] addUser called', { userData });
  try {
    const now = new Date();
    const userToCreate = removeUndefined({
      // uid: '', // Optionally leave blank or generate a temp id
      email: userData.email,
      displayName: userData.displayName,
      username: userData.username || userData.email.split('@')[0],
      status: userData.status,
      contacts: userData.contacts || [],
      roles: userData.roles,
      workspace: userData.workspace,
      language: userData.language,
      currency: userData.currency,
      profilePicture: userData.profilePicture,
      phoneNumber: userData.phoneNumber,
      invited: false,
      accepted: false,
      activityRecords: userData.activityRecords || [],
      createdAt: now,
      lastLogin: now
    });

    // Create Firestore user document
    const usersRef = collection(db, 'users');
    const docRef = await addDoc(usersRef, userToCreate);

    // Return properly constructed User object
    return {
      id: docRef.id,
      // uid: '', // Optionally leave blank or generate a temp id
      email: userData.email,
      displayName: userData.displayName,
      username: userData.username || userData.email.split('@')[0],
      status: userData.status,
      createdAt: now,
      lastLogin: now,
      contacts: userData.contacts || [],
      roles: userData.roles,
      workspace: userData.workspace,
      language: userData.language,
      currency: userData.currency,
      profilePicture: userData.profilePicture,
      phoneNumber: userData.phoneNumber,
      invited: false,
      accepted: false,
      activityRecords: userData.activityRecords || []
    };
  } catch (error: any) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('USER_EXISTS');
    }
    throw error;
  }
};

export const updateUser = async (
  id: string,
  userData: UpdateUserInput,
  firestoreUser: User
): Promise<void> => {
  console.log('[API] updateUser called', { id, userData, firestoreUser });
  try {
    const userRef = doc(db, 'users', id);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const activityRecord = createActivityRecord('updated', userData, firestoreUser);

    await updateDoc(userRef, removeUndefined({
      ...userData,
      activityRecords: arrayUnion(activityRecord)
    }));
  } catch (error) {
    console.error('Error updating user:', error);
    throw new Error('Failed to update user');
  }
};

export const deleteUser = async (
  id: string,
  firestoreUser: User
): Promise<void> => {
  console.log('[API] deleteUser called', { id, firestoreUser });
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Get the user document directly
    const userRef = doc(db, 'users', id);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    if (userData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('User not found in current workspace');
    }

    const activityRecord = createActivityRecord('deleted', userData, firestoreUser);

    // Add the activity record before deletion
    await updateDoc(userRef, {
      activityRecords: arrayUnion(activityRecord)
    });

    // Delete the document
    await deleteDoc(userRef);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw new Error('Failed to delete user');
  }
};

export const getUsersByRole = async (role: string, firestoreUser: User): Promise<User[]> => {
  console.log('[API] getUsersByRole called', { role, firestoreUser });
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('workspace.id', '==', firestoreUser.workspace.id),
      where('roles', 'array-contains', role)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        uid: data.uid,
        email: data.email,
        displayName: data.displayName,
        username: data.username,
        status: data.status || 'active',
        roles: data.roles || [],
        contacts: data.contacts || [],
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate(),
        workspace: {
          id: data.workspace.id,
          name: data.workspace.name,
          slug: data.workspace.slug
        },
        language: data.language || 'es',
        currency: data.currency || 'CLP',
        profilePicture: data.profilePicture,
        phoneNumber: data.phoneNumber,
        activityRecords: data.activityRecords || []
      } as User;
    });
  } catch (error) {
    console.error('Error fetching users by role:', error);
    throw new Error('Failed to fetch users by role');
  }
};

export const getUsersByWorkspace = async (workspaceId: string, firestoreUser: User): Promise<User[]> => {
  console.log('[API] getUsersByWorkspace called', { workspaceId, firestoreUser });
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('workspace.id', '==', workspaceId)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        uid: data.uid,
        email: data.email,
        displayName: data.displayName,
        username: data.username,
        status: data.status || 'active',
        roles: data.roles || [],
        contacts: data.contacts || [],
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate(),
        workspace: {
          id: data.workspace.id,
          name: data.workspace.name,
          slug: data.workspace.slug
        },
        language: data.language || 'es',
        currency: data.currency || 'CLP',
        profilePicture: data.profilePicture,
        phoneNumber: data.phoneNumber,
        activityRecords: data.activityRecords || []
      } as User;
    });
  } catch (error) {
    console.error('Error fetching users by workspace:', error);
    throw new Error('Failed to fetch users by workspace');
  }
};

export const getUser = async (userId: string, firestoreUser: User): Promise<User> => {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('Demo mode: Returning mock user data', { userId });
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Create mock user data based on the resident data
    // We'll create a mock user that matches the resident's user info
    const mockUser: User = {
      id: userId,
      uid: userId,
      email: `resident${userId.split('-').pop()}@demo.com`,
      displayName: `Resident ${userId.split('-').pop()}`,
      username: `resident${userId.split('-').pop()}`,
      status: 'active',
      roles: ['resident'],
      contacts: [],
      createdAt: new Date(),
      lastLogin: new Date(),
      workspace: {
        id: 'demo-workspace',
        name: 'Sunset Gardens',
        slug: 'demo-workspace'
      },
      language: 'en',
      currency: 'USD',
      profilePicture: undefined,
      phoneNumber: `+91${9000000000 + parseInt(userId.split('-').pop() || '0')}`,
      invited: false,
      accepted: true,
      activityRecords: []
    };
    
    return mockUser;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    if (userData.workspace.id !== firestoreUser.workspace.id) {
      throw new Error('User not found in current workspace');
    }

    return {
      id: userDoc.id,
      ...userData
    } as User;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
};

export const getUserByEmail = async (email: string): Promise<User[]> => {
  console.log('[API] getUserByEmail called', { email });
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        uid: data.uid,
        email: data.email,
        displayName: data.displayName,
        username: data.username,
        status: data.status || 'active',
        roles: data.roles || [],
        contacts: data.contacts || [],
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate(),
        workspace: data.workspace ? {
          id: data.workspace.id,
          name: data.workspace.name,
          slug: data.workspace.slug
        } : undefined,
        language: data.language || 'es',
        currency: data.currency || 'CLP',
        profilePicture: data.profilePicture,
        phoneNumber: data.phoneNumber,
        activityRecords: data.activityRecords || []
      } as User;
    });
  } catch (error) {
    console.error('Error fetching user by email:', error);
    throw new Error('Failed to fetch user by email');
  }
};

/**
 * Accepts an invitation and creates/updates the user account
 * @param invitationToken The invitation token
 * @param userData User data from the form
 * @returns A promise that resolves when the invitation is accepted
 */
export const acceptInvitation = async (
  invitationToken: string,
  userData: {
    email: string;
    displayName: string;
    password: string;
  }
): Promise<void> => {
  try {
    // First, get the invitation document
    const invitationRef = doc(db, 'invitations', invitationToken);
    const invitationDoc = await getDoc(invitationRef);
    
    if (!invitationDoc.exists()) {
      throw new Error('Invalid invitation token');
    }
    
    const invitationData = invitationDoc.data();
    
    // Check if invitation is expired
    if (invitationData.expiresAt?.toDate() < new Date()) {
      throw new Error('This invitation has expired');
    }
    
    // Check if invitation is already accepted
    if (invitationData.status !== 'pending') {
      throw new Error('This invitation has already been used');
    }
    
    // Create Firebase Auth user
    const { createUserWithEmailAndPassword } = await import('firebase/auth');
    const { auth } = await import('../firebase');
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      userData.email,
      userData.password
    );
    
    // Update invitation status
    await updateDoc(invitationRef, {
      status: 'accepted',
      acceptedAt: new Date(),
      acceptedBy: userCredential.user.uid
    });
    
    // Create/update user document with all required fields
    const userRef = doc(db, 'users', userCredential.user.uid);
    
    const userDocumentData = {
      id: userCredential.user.uid,
      uid: userCredential.user.uid,
      email: userData.email,
      displayName: userData.displayName,
      username: userData.displayName.toLowerCase().replace(/\s+/g, ''),
      status: 'active' as const,
      createdAt: new Date(),
      lastLogin: new Date(),
      contacts: [],
      roles: [invitationData.role as any],
      workspace: invitationData.workspace,
      language: 'en' as const,
      currency: 'USD' as const,
      invited: true,
      accepted: true,
      acceptedAt: new Date()
    };
    
    // Always use setDoc with merge to ensure all fields are set correctly
    const { setDoc } = await import('firebase/firestore');
    console.log('[acceptInvitation] Creating user document with data:', userDocumentData);
    await setDoc(userRef, userDocumentData, { merge: true });
    console.log('[acceptInvitation] User document created successfully');
    
    // Add a small delay to ensure the document is fully written
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Double-check that the accepted fields are set by doing an additional update
    console.log('[acceptInvitation] Updating accepted fields...');
    await updateDoc(userRef, {
      accepted: true,
      acceptedAt: new Date(),
      invited: true
    });
    console.log('[acceptInvitation] Accepted fields updated successfully');
    
    // Add another small delay before verification
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Verify that the fields were set correctly
    console.log('[acceptInvitation] Verifying user document...');
    const verificationDoc = await getDoc(userRef);
    if (verificationDoc.exists()) {
      const userData = verificationDoc.data();
      console.log('[acceptInvitation] User document verification:', {
        accepted: userData.accepted,
        acceptedAt: userData.acceptedAt,
        invited: userData.invited,
        uid: userData.uid,
        email: userData.email
      });
    }
    
  } catch (error) {
    console.error('Error accepting invitation:', error);
    throw error;
  }
};

// Bulk upload users using Firestore batch operations
export async function addUsersBulk(
  users: CreateUserInput[],
  firestoreUser: User,
  onProgress?: (uploaded: number, total: number) => void
): Promise<{ successCount: number; errorCount: number; createdUsers: User[]; errors: string[] }> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate progress for demo mode
    for (let i = 0; i < users.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 50));
      onProgress?.(i + 1, users.length);
    }
    
    // Return mock created users
    const createdUsers: User[] = users.map((userData, index) => ({
      id: `demo-user-${index}`,
      uid: `demo-user-${index}`,
      email: userData.email,
      displayName: userData.displayName,
      username: userData.username || userData.email.split('@')[0],
      status: userData.status || 'active',
      roles: userData.roles || [],
      contacts: userData.contacts || [],
      createdAt: new Date(),
      lastLogin: new Date(),
      workspace: userData.workspace,
      language: userData.language || 'en',
      currency: userData.currency || 'USD',
      profilePicture: userData.profilePicture,
      phoneNumber: userData.phoneNumber,
      invited: false,
      accepted: false,
      activityRecords: []
    }));
    
    return { 
      successCount: users.length, 
      errorCount: 0, 
      createdUsers, 
      errors: [] 
    };
  }
  
  try {
    const CHUNK_SIZE = 500; // Firestore batch limit
    const chunks = [];
    
    // Split records into chunks of 500
    for (let i = 0; i < users.length; i += CHUNK_SIZE) {
      chunks.push(users.slice(i, i + CHUNK_SIZE));
    }

    let totalUploaded = 0;
    const createdUsers: User[] = [];
    const errors: string[] = [];
    
    // Process each chunk with progress updates
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      const batch = writeBatch(db);
      const chunkUsers: User[] = [];
      
      chunk.forEach((userData) => {
        const userRef = doc(collection(db, 'users'));
        const now = new Date();
        
        const userToCreate = removeUndefined({
          uid: '', // Will be set when user authenticates
          email: userData.email,
          displayName: userData.displayName,
          username: userData.username || userData.email.split('@')[0],
          status: userData.status || 'active',
          contacts: userData.contacts || [],
          roles: userData.roles || [],
          workspace: userData.workspace,
          language: userData.language || 'en',
          currency: userData.currency || 'USD',
          profilePicture: userData.profilePicture,
          phoneNumber: userData.phoneNumber,
          invited: false,
          accepted: false,
          activityRecords: userData.activityRecords || [],
          createdAt: now,
          lastLogin: now
        });
        
        batch.set(userRef, userToCreate);
        
        // Store the user data with the generated ID for return
        chunkUsers.push({
          id: userRef.id,
          uid: '',
          email: userData.email,
          displayName: userData.displayName,
          username: userData.username || userData.email.split('@')[0],
          status: userData.status || 'active',
          roles: userData.roles || [],
          contacts: userData.contacts || [],
          createdAt: now,
          lastLogin: now,
          workspace: userData.workspace,
          language: userData.language || 'en',
          currency: userData.currency || 'USD',
          profilePicture: userData.profilePicture,
          phoneNumber: userData.phoneNumber,
          invited: false,
          accepted: false,
          activityRecords: userData.activityRecords || []
        });
      });

      try {
        await batch.commit();
        createdUsers.push(...chunkUsers);
        totalUploaded += chunk.length;
        onProgress?.(totalUploaded, users.length);
      } catch (error) {
        console.error(`Error uploading user chunk ${chunkIndex + 1}:`, error);
        errors.push(`User chunk ${chunkIndex + 1}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    const successCount = totalUploaded;
    const errorCount = users.length - totalUploaded;

    return { successCount, errorCount, createdUsers, errors };
  } catch (error) {
    console.error('Error in bulk user upload:', error);
    throw new Error('Failed to upload users in bulk');
  }
} 